import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  persistenceGovernanceApi,
  type ArchiveRecord,
  type AuditMetadata,
  type CQRSModel,
  type MigrationRecord,
  type PersistenceDashboard,
  type PersistenceHealth,
  type RegisteredRepository,
  type RetentionPolicy,
  type TransactionLogEntry,
} from '../../api/persistence';

type Tab = 'overview' | 'repositories' | 'migrations' | 'transactions' | 'archives' | 'retention' | 'cqrs' | 'audit' | 'health';
type Theme = 'light' | 'dark';

function statusColor(status: string, dark: boolean) {
  if (status === 'completed' || status === 'committed') return dark ? 'text-emerald-400' : 'text-emerald-600';
  if (status === 'pending') return dark ? 'text-amber-400' : 'text-amber-600';
  if (status === 'rolled_back' || status === 'failed') return dark ? 'text-red-400' : 'text-red-600';
  return dark ? 'text-slate-400' : 'text-slate-500';
}

export function PersistenceGovernancePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<PersistenceDashboard | null>(null);
  const [repos, setRepos] = useState<RegisteredRepository[]>([]);
  const [migrations, setMigrations] = useState<MigrationRecord[]>([]);
  const [transactions, setTransactions] = useState<TransactionLogEntry[]>([]);
  const [archives, setArchives] = useState<ArchiveRecord[]>([]);
  const [retention, setRetention] = useState<RetentionPolicy[]>([]);
  const [cqrs, setCqrs] = useState<CQRSModel[]>([]);
  const [audit, setAudit] = useState<AuditMetadata[]>([]);
  const [health, setHealth] = useState<PersistenceHealth | null>(null);
  const [serviceFilter, setServiceFilter] = useState('');
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, repoList, migs, txs, arcs, ret, cqrsList, auditList, hlth] = await Promise.all([
        persistenceGovernanceApi.dashboard(),
        persistenceGovernanceApi.listRepositories(),
        persistenceGovernanceApi.listMigrations(),
        persistenceGovernanceApi.listTransactions(),
        persistenceGovernanceApi.listArchives(),
        persistenceGovernanceApi.listRetention(),
        persistenceGovernanceApi.listCQRS(),
        persistenceGovernanceApi.listAudit(),
        persistenceGovernanceApi.health(),
      ]);
      setDashboard(dash);
      setRepos(repoList);
      setMigrations(migs);
      setTransactions(txs);
      setArchives(arcs);
      setRetention(ret);
      setCqrs(cqrsList);
      setAudit(auditList);
      setHealth(hlth);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load persistence platform');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const runMigration = async (service?: string, dryRun = false) => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await persistenceGovernanceApi.runMigration({ service, dry_run: dryRun });
      setActionMsg(result.message);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Migration run failed');
    } finally {
      setBusy(false);
    }
  };

  const services = [...new Set(repos.map((r) => r.service))];
  const filteredRepos = serviceFilter ? repos.filter((r) => r.service === serviceFilter) : repos;
  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'repositories', label: 'Repository Explorer' },
    { id: 'migrations', label: 'Migration Console' },
    { id: 'transactions', label: 'Transaction Monitor' },
    { id: 'archives', label: 'Archive Manager' },
    { id: 'retention', label: 'Retention Policies' },
    { id: 'cqrs', label: 'CQRS Models' },
    { id: 'audit', label: 'Audit Explorer' },
    { id: 'health', label: 'Health Dashboard' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-violet-500">DEV-000081</p>
          <h1 className="text-2xl font-semibold">Enterprise Persistence Platform</h1>
          <p className={`text-sm ${muted}`}>Repository framework, CQRS, transactions, migrations & data lifecycle governance</p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/integration-governance" className="text-violet-500 hover:underline">Integration Governance</Link>
            <span className={muted}>·</span>
            <Link to="/platform/event-mesh" className="text-violet-500 hover:underline">Event Mesh</Link>
            <span className={muted}>·</span>
            <Link to="/platform/api-gateway" className="text-violet-500 hover:underline">API Gateway</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
          <button type="button" onClick={refresh} disabled={busy} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50">Refresh</button>
        </div>
      </div>

      {error && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-red-900 bg-red-950 text-red-300' : 'border-red-200 bg-red-50 text-red-700'}`}>{error}</div>}
      {actionMsg && <div className={`rounded-lg border px-4 py-3 text-sm ${dark ? 'border-emerald-900 bg-emerald-950 text-emerald-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800'}`}>{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b pb-2" role="tablist">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${tab === t.id ? 'bg-violet-600 text-white' : dark ? 'text-slate-400 hover:bg-slate-800' : 'text-slate-600 hover:bg-slate-100'}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'overview' && health && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Persistence Health Score</p><p className="mt-1 text-3xl font-semibold text-violet-500">{health.persistence_health_score.toFixed(1)}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Registered Repositories</p><p className="mt-1 text-3xl font-semibold">{dashboard.repositories}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Pending Migrations</p><p className="mt-1 text-3xl font-semibold text-amber-500">{health.pending_migrations}</p></div>
            <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Transaction Success</p><p className="mt-1 text-3xl font-semibold">{health.transaction_success_rate.toFixed(1)}%</p></div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h2 className="font-medium">Extension Points</h2>
            <ul className={`mt-2 space-y-1 text-sm ${muted}`}>
              {dashboard.extension_points.map((ep) => (
                <li key={ep.dev_id}><span className="font-mono text-violet-500">{ep.dev_id}</span> — {ep.name}: {ep.description}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {tab === 'repositories' && (
        <div className="space-y-4">
          <select value={serviceFilter} onChange={(e) => setServiceFilter(e.target.value)} className={`rounded-lg border px-3 py-2 text-sm ${card}`} aria-label="Filter by service">
            <option value="">All services</option>
            {services.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="overflow-x-auto rounded-xl border">
            <table className={`min-w-full text-sm ${card}`}>
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Service</th>
                  <th className="px-4 py-2 text-left">Kind</th>
                  <th className="px-4 py-2 text-left">Storage</th>
                  <th className="px-4 py-2 text-left">Isolation</th>
                  <th className="px-4 py-2 text-left">CQRS</th>
                </tr>
              </thead>
              <tbody>
                {filteredRepos.map((r) => (
                  <tr key={r.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                    <td className="px-4 py-2 font-mono text-xs">{r.name}</td>
                    <td className="px-4 py-2">{r.service}</td>
                    <td className="px-4 py-2">{r.kind}</td>
                    <td className="px-4 py-2">{r.storage_type}</td>
                    <td className="px-4 py-2">{r.isolation_mode}</td>
                    <td className="px-4 py-2">{r.supports_cqrs ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'migrations' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy} onClick={() => runMigration('forecastengine')} className="rounded-lg bg-violet-600 px-3 py-2 text-sm text-white hover:bg-violet-700 disabled:opacity-50">Run Pending (forecastengine)</button>
            <button type="button" disabled={busy} onClick={() => runMigration(undefined, true)} className={`rounded-lg border px-3 py-2 text-sm ${card}`}>Dry Run All Pending</button>
          </div>
          <div className="overflow-x-auto rounded-xl border">
            <table className={`min-w-full text-sm ${card}`}>
              <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
                <tr>
                  <th className="px-4 py-2 text-left">Service</th>
                  <th className="px-4 py-2 text-left">Version</th>
                  <th className="px-4 py-2 text-left">Name</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-left">Tenant Aware</th>
                </tr>
              </thead>
              <tbody>
                {migrations.map((m) => (
                  <tr key={m.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                    <td className="px-4 py-2">{m.service}</td>
                    <td className="px-4 py-2">{m.version}</td>
                    <td className="px-4 py-2 font-mono text-xs">{m.name}</td>
                    <td className={`px-4 py-2 ${statusColor(m.status, dark)}`}>{m.status}</td>
                    <td className="px-4 py-2">{m.tenant_aware ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'transactions' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 text-left">Service</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Operations</th>
                <th className="px-4 py-2 text-left">Retries</th>
                <th className="px-4 py-2 text-left">Correlation</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2">{tx.service}</td>
                  <td className={`px-4 py-2 ${statusColor(tx.status, dark)}`}>{tx.status}</td>
                  <td className="px-4 py-2">{tx.operations}</td>
                  <td className="px-4 py-2">{tx.retry_count}</td>
                  <td className="px-4 py-2 font-mono text-xs">{tx.correlation_id ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'archives' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 text-left">Aggregate</th>
                <th className="px-4 py-2 text-left">Entity</th>
                <th className="px-4 py-2 text-left">Archived</th>
                <th className="px-4 py-2 text-left">Retention Days</th>
                <th className="px-4 py-2 text-left">Legal Hold</th>
              </tr>
            </thead>
            <tbody>
              {archives.map((a) => (
                <tr key={a.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2">{a.aggregate}</td>
                  <td className="px-4 py-2 font-mono text-xs">{a.entity_id}</td>
                  <td className="px-4 py-2">{new Date(a.archived_at).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{a.retention_days}</td>
                  <td className="px-4 py-2">{a.legal_hold ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'retention' && (
        <div className="grid gap-4 sm:grid-cols-2">
          {retention.map((p) => (
            <div key={p.id} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-medium">{p.name}</h3>
              <p className={`text-sm ${muted}`}>Aggregate: {p.aggregate}</p>
              <p className={`mt-2 text-sm ${muted}`}>Retain {p.retain_days}d · Archive {p.archive_days}d · Purge after {p.purge_after_days}d</p>
              <p className={`mt-1 text-xs ${p.enabled ? 'text-emerald-500' : muted}`}>{p.enabled ? 'Enabled' : 'Disabled'}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'cqrs' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Service</th>
                <th className="px-4 py-2 text-left">Type</th>
                <th className="px-4 py-2 text-left">Materialized</th>
                <th className="px-4 py-2 text-left">Event Driven</th>
              </tr>
            </thead>
            <tbody>
              {cqrs.map((c) => (
                <tr key={c.id} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2">{c.name}</td>
                  <td className="px-4 py-2">{c.service}</td>
                  <td className="px-4 py-2">{c.model_type}</td>
                  <td className="px-4 py-2">{c.materialized ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2">{c.event_driven ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'audit' && (
        <div className="overflow-x-auto rounded-xl border">
          <table className={`min-w-full text-sm ${card}`}>
            <thead className={dark ? 'bg-slate-800' : 'bg-slate-50'}>
              <tr>
                <th className="px-4 py-2 text-left">Entity</th>
                <th className="px-4 py-2 text-left">Created By</th>
                <th className="px-4 py-2 text-left">Modified By</th>
                <th className="px-4 py-2 text-left">Change Reason</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => (
                <tr key={`${a.entity_type}-${a.entity_id}`} className={dark ? 'border-t border-slate-800' : 'border-t border-slate-100'}>
                  <td className="px-4 py-2">{a.entity_type}/{a.entity_id}</td>
                  <td className="px-4 py-2">{a.created_by}</td>
                  <td className="px-4 py-2">{a.modified_by ?? a.deleted_by ?? '—'}</td>
                  <td className="px-4 py-2">{a.change_reason ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'health' && health && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Migration Success</p><p className="mt-1 text-2xl font-semibold">{health.migration_success_rate.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Rollback Frequency</p><p className="mt-1 text-2xl font-semibold">{health.rollback_frequency.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Avg Query Latency</p><p className="mt-1 text-2xl font-semibold">{health.avg_query_latency_ms.toFixed(1)} ms</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Storage Growth</p><p className="mt-1 text-2xl font-semibold">{health.storage_growth_gb.toFixed(1)} GB</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Archive Utilization</p><p className="mt-1 text-2xl font-semibold">{health.archive_utilization_pct.toFixed(1)}%</p></div>
          <div className={`rounded-xl border p-4 ${card}`}><p className={`text-xs uppercase ${muted}`}>Validated At</p><p className="mt-1 text-sm">{new Date(health.validated_at).toLocaleString()}</p></div>
        </div>
      )}
    </div>
  );
}
